// src/components/admin/UserRolesManager.js
import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useClaims } from '../../hooks/useClaims';
import { roleInvitationService } from '../../services/roleInvitationService';
import { UserService } from '../../services/userService';
import { 
  Search, Shield, UserCog, BookOpen, X, Check, Loader2, 
  Mail, Send, Clock, UserPlus, AlertCircle 
} from 'lucide-react';

const UserRolesManager = ({ bookId = null, bookName = '', onRoleChange = null }) => {
  const { permissions, loading: claimsLoading } = useClaims(bookId);
  const [users, setUsers] = useState([]);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);
  const [selectedRole, setSelectedRole] = useState('viewer');

  const isSuperAdmin = permissions.isSuperAdmin;
  const canManageRoles = bookId 
    ? (permissions.isSuperAdmin || permissions.canManage)
    : permissions.isSuperAdmin;

  useEffect(() => {
    if (claimsLoading) return;
    
    if (isSuperAdmin) {
      loadAllUsers();
    }
    if (bookId) {
      loadPendingInvitations();
    }
    setLoading(false);
  }, [isSuperAdmin, bookId, claimsLoading]);

  const loadAllUsers = async () => {
    try {
      const q = query(collection(db, 'pb-users'), orderBy('createdAt', 'desc'), limit(50));
      const snapshot = await getDocs(q);
      setUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadPendingInvitations = async () => {
    try {
      const invitations = await roleInvitationService.getPendingInvitations(bookId);
      setPendingInvitations(invitations);
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };

  const handleEmailSearch = async () => {
    if (!searchEmail.trim()) return;
    
    setSearching(true);
    setSearchResult(null);
    
    try {
      const users = await UserService.searchUsersByEmail(searchEmail.trim());
      if (users.length > 0) {
        setSearchResult({ type: 'user', data: users[0] });
      } else {
        // No user found - can still send invitation to email
        setSearchResult({ type: 'email', data: { email: searchEmail.trim() } });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Search failed' });
    } finally {
      setSearching(false);
    }
  };

  const handleSendInvitation = async (targetEmail, targetUserId = null) => {
    if (!bookId) {
      setMessage({ type: 'error', text: 'Book ID is required' });
      return;
    }
    
    setActionLoading(targetEmail);
    try {
      await roleInvitationService.sendRoleInvitation({
        bookId,
        bookName: bookName || 'Untitled Book', // Fallback
        targetEmail,
        targetUserId,
        role: selectedRole
      });
      
      setMessage({ type: 'success', text: `Invitation sent to ${targetEmail}` });
      setSearchEmail('');
      setSearchResult(null);
      loadPendingInvitations();
      
      // Notify parent
      if (onRoleChange) onRoleChange();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    setActionLoading(invitationId);
    try {
      await roleInvitationService.cancelInvitation(invitationId);
      setMessage({ type: 'success', text: 'Invitation cancelled' });
      loadPendingInvitations();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setActionLoading(null);
    }
  };

  // Super admin: direct role assignment
  const handleDirectRoleAssign = async (userId, role, isBookRole = false) => {
    setActionLoading(userId);
    try {
      if (isBookRole && bookId) {
        if (!role || role === '') {
          await roleInvitationService.removeBookRole(userId, bookId);
        } else {
          await roleInvitationService.directAssignBookRole(userId, bookId, role);
        }
        
        // ✅ Optimistic local update for book role
        setUsers(prev => prev.map(u => {
          if (u.id !== userId) return u;
          const updatedBookRoles = { ...(u.claims?.bookRoles || {}) };
          if (!role || role === '') {
            delete updatedBookRoles[bookId];
          } else {
            updatedBookRoles[bookId] = role;
          }
          return {
            ...u,
            claims: { ...u.claims, bookRoles: updatedBookRoles }
          };
        }));
        
      } else {
        await roleInvitationService.directAssignSystemRole(userId, role);
        
        // ✅ Optimistic local update for system role
        setUsers(prev => prev.map(u => 
          u.id === userId 
            ? { ...u, claims: { ...u.claims, role } }
            : u
        ));
      }
      
      setMessage({ type: 'success', text: 'Role updated' });
      if (onRoleChange) onRoleChange();
      
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      loadAllUsers(); // Revert on error
    } finally {
      setActionLoading(null);
    }
  };

  if (claimsLoading || loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!canManageRoles) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700">You don't have permission to manage roles</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border">
      {/* Header */}
      <div className="flex items-center gap-3 p-6 border-b">
        <UserCog className="w-6 h-6 text-blue-600" />
        <div>
          <h2 className="text-xl font-semibold">
            {bookId ? 'Book Collaborators' : 'User Roles Manager'}
          </h2>
          <p className="text-sm text-gray-500">
            {isSuperAdmin ? 'Full access - can assign roles directly' : 'Send invitations to add collaborators'}
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mx-6 mt-4 p-3 rounded-lg flex items-center justify-between ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Search & Invite Section */}
      <div className="p-6 border-b bg-gray-50">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              placeholder="Enter email to search or invite..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEmailSearch()}
              className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          
          {bookId && (
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="px-3 py-2.5 border rounded-lg bg-white"
            >
              <option value="viewer">Viewer</option>
              <option value="contributor">Contributor</option>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
          )}
          
          <button
            onClick={handleEmailSearch}
            disabled={searching || !searchEmail.trim()}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </button>
        </div>

        {/* Search Result */}
        {searchResult && (
          <div className="mt-4 p-4 bg-white border rounded-lg">
            {searchResult.type === 'user' ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-medium">
                      {searchResult.data.displayName?.[0] || searchResult.data.email?.[0] || '?'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{searchResult.data.displayName || 'No name'}</p>
                    <p className="text-sm text-gray-500">{searchResult.data.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleSendInvitation(searchResult.data.email, searchResult.data.id)}
                  disabled={actionLoading === searchResult.data.email}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                >
                  {actionLoading === searchResult.data.email ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send Invitation
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium">{searchResult.data.email}</p>
                    <p className="text-sm text-amber-600">User not registered yet - invitation will be sent via email</p>
                  </div>
                </div>
                <button
                  onClick={() => handleSendInvitation(searchResult.data.email)}
                  disabled={actionLoading === searchResult.data.email}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                >
                  {actionLoading === searchResult.data.email ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  Invite via Email
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      {bookId && pendingInvitations.length > 0 && (
        <div className="p-6 border-b">
          <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pending Invitations ({pendingInvitations.length})
          </h3>
          <div className="space-y-2">
            {pendingInvitations.map(inv => (
              <div key={inv.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div>
                  <p className="font-medium">{inv.targetEmail}</p>
                  <p className="text-sm text-gray-500">
                    Role: <span className="capitalize">{inv.role}</span> • 
                    Sent {new Date(inv.createdAt?.toDate?.() || inv.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => handleCancelInvitation(inv.id)}
                  disabled={actionLoading === inv.id}
                  className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1"
                >
                  {actionLoading === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Super Admin: All Users List */}
      {isSuperAdmin && (
        <div className="p-6">
          <h3 className="font-medium text-gray-700 mb-4">All Users</h3>
          <div className="space-y-3">
            {users.map(user => (
              <div key={user.id} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{user.displayName || 'No name'}</p>
                    <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {user.claims?.role && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          user.claims.role === 'super-admin' ? 'bg-purple-100 text-purple-700' :
                          user.claims.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {user.claims.role}
                        </span>
                      )}
                      {bookId && user.claims?.bookRoles?.[bookId] && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                          Book: {user.claims.bookRoles[bookId]}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    {/* System Role */}
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-gray-400" />
                      <select
                        value={user.claims?.role || 'user'}
                        onChange={(e) => handleDirectRoleAssign(user.id, e.target.value, false)}
                        disabled={actionLoading === user.id}
                        className="text-sm border rounded px-2 py-1 bg-white"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="super-admin">Super Admin</option>
                      </select>
                    </div>

                    {/* Book Role */}
                    {bookId && (
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-gray-400" />
                        <select
                          value={user.claims?.bookRoles?.[bookId] || ''}
                          onChange={(e) => handleDirectRoleAssign(user.id, e.target.value, true)}
                          disabled={actionLoading === user.id}
                          className="text-sm border rounded px-2 py-1 bg-white"
                        >
                          <option value="">No Access</option>
                          <option value="viewer">Viewer</option>
                          <option value="contributor">Contributor</option>
                          <option value="editor">Editor</option>
                          <option value="admin">Admin</option>
                          <option value="owner">Owner</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {actionLoading === user.id && (
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRolesManager;